<template>
    <div class="login-container">
      <el-card class="login-card">
        <div class="logo-container">
          <img src="../assets/DiCloudAccess.svg" class="logo">
        </div>
        <h2 class="login-title">点云通软件管理系统</h2>
        <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" label-position="top">
          <el-form-item prop="username">
            <el-input v-model="loginForm.username" placeholder="用户名">
              <template #prefix>
                <el-icon><User /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item prop="password">
            <el-input v-model="loginForm.password" :type="passwordVisible ? 'text' : 'password'" placeholder="密码" @keyup.enter="handleLogin">
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
              <template #suffix>
                <el-icon class="password-eye" @click="passwordVisible = !passwordVisible">
                  <View v-if="passwordVisible" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleLogin" :loading="loading" class="login-button">登录</el-button>
          </el-form-item>
        </el-form>
      </el-card>
      
      <el-dialog
        v-model="showChangePasswordDialog"
        title="首次登录修改密码"
        width="400px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false">
        <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-position="top">
          <el-form-item label="新密码" prop="newPassword">
            <el-input v-model="passwordForm.newPassword" :type="newPasswordVisible ? 'text' : 'password'" placeholder="请输入新密码">
              <template #suffix>
                <el-icon class="password-eye" @click="newPasswordVisible = !newPasswordVisible">
                  <View v-if="newPasswordVisible" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input v-model="passwordForm.confirmPassword" :type="confirmPasswordVisible ? 'text' : 'password'" placeholder="请再次输入新密码">
              <template #suffix>
                <el-icon class="password-eye" @click="confirmPasswordVisible = !confirmPasswordVisible">
                  <View v-if="confirmPasswordVisible" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="changePassword" :loading="changingPassword" style="width: 100%;">确认修改</el-button>
          </el-form-item>
        </el-form>
      </el-dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { ElMessage } from 'element-plus'
  import { User, Lock, View, Hide } from '@element-plus/icons-vue'
  import { useHead } from '@vueuse/head'
  import authApi from '../js/connect.js'
  import { clearLoginState, getAccessToken, isTokenValid, setLoginState } from '../js/auth'
  
  useHead({
    title: '登录',
  })
  
  const router = useRouter()
  const loginFormRef = ref(null)
  const passwordFormRef = ref(null)
  const loading = ref(false)
  const changingPassword = ref(false)
  const showChangePasswordDialog = ref(false)
  const passwordVisible = ref(false)
  const newPasswordVisible = ref(false)
  const confirmPasswordVisible = ref(false)
  
  const loginForm = reactive({
    username: '',
    password: ''
  })
  
  const passwordForm = reactive({
    newPassword: '',
    confirmPassword: ''
  })
  
  const loginRules = {
    username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
    password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
  }
  
  const passwordRules = {
    newPassword: [
      { required: true, message: '请输入新密码', trigger: 'blur' },
      { min: 8, message: '密码长度至少为8个字符', trigger: 'blur' }
    ],
    confirmPassword: [
      { required: true, message: '请再次输入新密码', trigger: 'blur' },
      { 
        validator: (rule, value, callback) => {
          if (value !== passwordForm.newPassword) {
            callback(new Error('两次输入的密码不一致'));
          } else {
            callback();
          }
        }, 
        trigger: 'blur' 
      }
    ]
  }
  
  onMounted(() => {
    const token = getAccessToken();
    if (token && isTokenValid(token)) {
      router.push('/welcome');
    } else if (token && !isTokenValid(token)) {
      clearLoginState();
    }
  })
  
  const handleLogin = async () => {
    if (!loginFormRef.value) return
    
    await loginFormRef.value.validate(async (valid) => {
      if (valid) {
        loading.value = true
        try {
          const response = await authApi.adminLogin(loginForm.username, loginForm.password);
          
          setLoginState({
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
            username: response.data.username,
            role: response.data.role
          });
          
          ElMessage.success('登录成功');
          
          // 如果是第一次登录且使用默认密码，弹出修改密码对话框
          if (loginForm.username === 'admin' && loginForm.password === 'admin123') {
            showChangePasswordDialog.value = true;
          } else {
            router.push('/welcome');
          }
        } catch (error) {
          let errorMsg = '登录失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          }
          ElMessage.error(errorMsg);
        } finally {
          loading.value = false;
        }
      } else {
        ElMessage.error('请填写所有必填字段');
      }
    });
  }
  
  const changePassword = async () => {
    if (!passwordFormRef.value) return;
    
    await passwordFormRef.value.validate(async (valid) => {
      if (valid) {
        changingPassword.value = true;
        try {
          await authApi.changePassword(loginForm.password, passwordForm.newPassword);
          ElMessage.success('密码修改成功');
          showChangePasswordDialog.value = false;
          router.push('/welcome');
        } catch (error) {
          let errorMsg = '密码修改失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          }
          ElMessage.error(errorMsg);
        } finally {
          changingPassword.value = false;
        }
      }
    });
  }
  </script>
  
  <style scoped>
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: var(--dca-bg);
  }
  
  .login-card {
    width: 350px;
    padding: 20px;
  }
  
  .logo-container {
    text-align: center;
    margin-bottom: 20px;
  }
  
  .logo {
    width: 80px;
    height: auto;
  }
  
  .login-title {
    text-align: center;
    margin-bottom: 30px;
    color: var(--dca-text);
    font-size: 24px;
  }
  
  .login-button {
    width: 100%;
  }
  
  .el-input {
    margin-bottom: 20px;
  }
  
  .password-eye {
    cursor: pointer;
  }
  </style>